<script setup>
import { ref, watch, onMounted, nextTick, computed, onUnmounted } from 'vue';
import Button from './Button.vue';

const props = defineProps({
  categories: { type: Array, required: true }
});

const activeCategory = ref(0);
const activeService = ref(0);
const tabsContainer = ref(null);
const activeIndicator = ref(null);
const isAnimating = ref(false);

// Auto-play functionality
const autoPlayActive = ref(true);
const autoPlayInterval = ref(null);
const autoPlayDuration = 5000; // 5 seconds per slide
const userInteracted = ref(false);

// Computed property to get the current category's services
const currentServices = computed(() => {
  return props.categories[activeCategory.value].services || [];
});

// Computed property for total number of services in the current category
const totalServices = computed(() => {
  return currentServices.value.length;
});

// Computed property for category description text
const categoryDescription = computed(() => {
  // Use the description from the actual category data if available
  if (props.categories[activeCategory.value] && props.categories[activeCategory.value].description) {
    return props.categories[activeCategory.value].description;
  }
  
  // Fallback descriptions based on category name
  const currentCategoryName = props.categories[activeCategory.value]?.name?.toLowerCase();
  
  const descriptions = {
    'webapplicaties': "Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat.",
    'mobile': "We maken slimme oplossingen die processen versnellen, systemen verbinden en data overzichtelijk maken. Denk aan maatwerksoftware die naadloos aansluit op jouw werkwijze.",
    'integraties': "Onze expertise ligt in het ontwikkelen van gebruiksvriendelijke interfaces en robuuste backends die samen een naadloze gebruikerservaring creëren.",
    'default': "Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat."
  };
  
  return descriptions[currentCategoryName] || descriptions['default'];
});

// Computed property for service description text
const serviceDescription = computed(() => {
  // Use the description from the actual service data if available
  const currentService = props.categories[activeCategory.value]?.services[activeService.value];
  
  if (currentService && currentService.description) {
    return currentService.description;
  }
  
  // Default fallback description
  return "Bij c-o-d-e-s bouwen we platformen die doen wat ze moeten doen – en méér. Schaalbaar wanneer je groeit, flexibel in gebruik, krachtig in resultaat.";
});

// Start auto-play
const startAutoPlay = () => {
  if (autoPlayInterval.value) clearInterval(autoPlayInterval.value);
  
  autoPlayInterval.value = setInterval(() => {
    if (autoPlayActive.value && !isAnimating.value) {
      navigateNext(true); // Pass true to indicate auto-navigation
    }
  }, autoPlayDuration);
};

// Stop auto-play
const stopAutoPlay = () => {
  autoPlayActive.value = false;
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value);
    autoPlayInterval.value = null;
  }
};

// Restart auto-play
const restartAutoPlay = () => {
  autoPlayActive.value = true;
  startAutoPlay();
};

// Reset active service when category changes
watch(activeCategory, () => {
  // Don't set isAnimating for category changes, only for service navigation
  activeService.value = 0;
  userInteracted.value = true; // Mark that user has interacted
  stopAutoPlay();
  nextTick(() => updateIndicatorPosition());
});

// Handle direct service selection
const selectService = (index) => {
  if (index !== activeService.value && !isAnimating.value) {
    stopAutoPlay();
    userInteracted.value = true;
    isAnimating.value = true;
    activeService.value = index;
    
    setTimeout(() => {
      isAnimating.value = false;
    }, 400); // Reduced timeout to match actual content transition
  }
};

// Navigation functions
const navigatePrev = () => {
  if (!isAnimating.value) {
    stopAutoPlay();
    userInteracted.value = true;
    isAnimating.value = true;
    
    // Circular navigation: go to last item if at first
    if (activeService.value === 0) {
      activeService.value = totalServices.value - 1;
    } else {
      activeService.value--;
    }
    
    setTimeout(() => {
      isAnimating.value = false;
    }, 400);
  }
};

const navigateNext = (isAuto = false) => {
  if (!isAnimating.value) {
    if (!isAuto) {
      stopAutoPlay();
      userInteracted.value = true;
    }
    
    isAnimating.value = true;
    
    // Circular navigation: go to first item if at last
    if (activeService.value >= totalServices.value - 1) {
      activeService.value = 0;
    } else {
      activeService.value++;
    }
    
    setTimeout(() => {
      isAnimating.value = false;
    }, 400);
  }
};

// Update the position of the active indicator
const updateIndicatorPosition = () => {
  if (!tabsContainer.value || !activeIndicator.value) return;
  
  const activeTab = tabsContainer.value.querySelector(`.tab-button[data-index="${activeCategory.value}"]`);
  if (activeTab) {
    const containerRect = tabsContainer.value.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    
    activeIndicator.value.style.left = `${tabRect.left - containerRect.left}px`;
    activeIndicator.value.style.width = `${tabRect.width}px`;
  }
};

// Handle scroll events to restart auto-play when user scrolls away
const handleScroll = () => {
  const section = document.getElementById('services-showcase');
  if (!section) return;
  
  const rect = section.getBoundingClientRect();
  const isInView = rect.top < window.innerHeight && rect.bottom > 0;
  
  // If user scrolled away and comes back, restart auto-play from current position
  if (isInView && userInteracted.value && !autoPlayActive.value) {
    userInteracted.value = false;
    restartAutoPlay();
  }
  // If user scrolled away, mark for potential restart
  else if (!isInView && autoPlayActive.value) {
    // Don't stop auto-play immediately, just mark that we're out of view
    // This allows auto-play to continue when user returns
  }
};

onMounted(() => {
  updateIndicatorPosition();
  startAutoPlay(); // Start auto-play when component mounts
  window.addEventListener('resize', updateIndicatorPosition);
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  stopAutoPlay();
  window.removeEventListener('resize', updateIndicatorPosition);
  window.removeEventListener('scroll', handleScroll);
});
</script>

<template>
  <section id="services-showcase" class="wide-container relative">
    <!-- Background decorations -->
    <div class="absolute inset-0 opacity-30 bg-[linear-gradient(to_right,#4f4f4f0a_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f0a_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]"></div>
    
    <div class="relative z-10 sm:text-center">
      <!-- Section heading -->
      <h2 class="text-pretty text-5xl font-semibold text-jet sm:text-6xl lg:text-7xl tracking-tight">
        <span class="bg-custom-rainbow-text text-transparent bg-clip-text">Slim ontwikkeld.<br></span>
        Krachtig in gebruik. Flexibel in alles.
      </h2>
      
      <!-- Featured image section - fixed height with centered image -->
      <div class="mx-auto w-full max-w-6xl my-6">
        <div class="relative h-96 rounded-lg overflow-hidden">
          <!-- Smooth crossfade transition for images -->
          <transition name="smooth-fade" mode="out-in" :duration="{ enter: 400, leave: 200 }">
            <img :key="`service-image-${activeCategory}-${activeService}`" 
                 :src="categories[activeCategory].services[activeService].image" 
                 :alt="categories[activeCategory].services[activeService].title" 
                 class="w-full h-full object-cover object-center" />
          </transition>
          
          <!-- Gradient overlay -->
          <div class="absolute inset-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent"></div>
          
          <!-- Text overlay with smooth text transitions -->
          <div class="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-left text-white z-10">
            <transition name="text-slide" mode="out-in" :duration="{ enter: 400, leave: 200 }">
              <h3 :key="`service-title-${activeCategory}-${activeService}`" 
                  class="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mt-1 drop-shadow-md mb-3">
                {{ categories[activeCategory].services[activeService].title }}
              </h3>
            </transition>
            
            <!-- Enhanced Pagination indicators with progress animation -->
            <div class="-mb-3 flex gap-2">
              <div v-for="i in totalServices" :key="i" 
                   class="relative block h-1.5 w-6 rounded-xs bg-gray-200/35 overflow-hidden cursor-pointer transition-all duration-300 hover:bg-orange-200/35"
                   @click="selectService(i-1)">
                <!-- Background bar -->
                <div class="absolute inset-0 bg-gray-200/35"></div>
                <!-- Progress bar that fills from left to right - only orange for active -->
                <div class="absolute inset-0 origin-left transition-all duration-300"
                     :class="i-1 === activeService ? 'bg-orange-500 animate-progress-fill' : 'bg-gray-400/60 scale-x-0'"
                     :style="i-1 === activeService && autoPlayActive && !userInteracted ? `animation-duration: ${autoPlayDuration}ms` : ''"></div>
              </div>
            </div>
          </div>
          
          <!-- Navigation controls - always enabled with consistent styling -->
          <div class="absolute bottom-6 right-6 flex items-center gap-3 z-20">
            <button @click="navigatePrev"
                    class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black/60 text-gray-200 hover:bg-black/80" 
                    aria-label="Previous service">
              <font-awesome-icon :icon="['fas', 'chevron-left']" class="h-4 w-4" />
            </button>
            
            <button @click="() => navigateNext(false)"
                    class="h-10 w-10 flex items-center justify-center rounded-full transition-all duration-300 bg-black/60 text-gray-200 hover:bg-black/80" 
                    aria-label="Next service">
              <font-awesome-icon :icon="['fas', 'chevron-right']" class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <!-- Modern tab navigation with animated underline -->
      <div class="relative mb-8">
        <div ref="tabsContainer" class="w-full flex justify-center flex-wrap sm:flex-nowrap px-0">
          <button v-for="(category, index) in categories" :key="`category-${index}`" @click="activeCategory = index" :class="['tab-button relative px-1 sm:px-3 py-2 text-sm font-medium whitespace-nowrap transition-all rounded-lg border sm:px-4 sm:py-3 sm:text-base sm:rounded-none sm:border-none', activeCategory === index ? 'bg-transparent text-orange-500 border-transparent' : 'text-gray-600 bg-transparent border-transparent hover:bg-transparent hover:text-gray-900']" :data-index="index">
            {{ category.name }}
          </button>
          <div ref="activeIndicator" class="hidden sm:block absolute bottom-0 h-0.5 bg-orange-500 transition-all duration-300 ease-in-out"></div>
        </div>
      </div>
      
      <!-- Dynamic service description with smooth transitions -->
      <transition name="text-slide" mode="out-in" :duration="{ enter: 400, leave: 200 }">
        <p :key="`service-desc-${activeCategory}-${activeService}`" class="text-base sm:text-lg md:text-xl text-balance text-gray-600 mb-6 min-h-[8em] sm:min-h-[6em] md:min-h-[4em] lg:min-h-[3em] xl:min-h-[2em]">
          {{ serviceDescription }}
        </p>
      </transition>
      
      <!-- Image caption with smooth text transitions -->
      <div class="justify-center w-full flex flex-col sm:flex-row items-center gap-4 mb-10">
        <Button :href="categories[activeCategory].services[activeService].ctaLink || '/contact'" variant="gradient" block="true" icon="arrow-right" iconPosition="right" class="button-text-container button-width-transition">
            <transition name="button-text" mode="out-in" :duration="{ enter: 300, leave: 150 }">
                <span :key="`cta-text-${activeCategory}-${activeService}`" class="inline-block">
                {{ categories[activeCategory].services[activeService].ctaText || 'Meer informatie' }}
                </span>
            </transition>
        </Button>
        <Button href="/contact" variant="white" block="true" icon="envelope">
          Vrijblijvend advies
        </Button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;  /* Chrome, Safari and Opera */
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.tab-button { transition: color 0.3s ease; }
.tab-button:focus { outline: none; }

/* Rounded edges for pagination indicators */
.rounded-xs {
  border-radius: 0.125rem;
}

/* Smooth crossfade animation for images */
.smooth-fade-enter-active {
  transition: all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.smooth-fade-leave-active {
  transition: all 200ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.smooth-fade-enter-from {
  opacity: 0;
  transform: scale(1.02);
}

.smooth-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* Smooth text transitions with slide effect */
.text-slide-enter-active {
  transition: all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 100ms;
}

.text-slide-leave-active {
  transition: all 200ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.text-slide-enter-from {
  opacity: 0;
  transform: translateY(15px);
}

.text-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* Ensure buttons maintain their state during transitions */
button:disabled {
  pointer-events: none;
  transition: all 300ms ease;
}

/* Progress fill animation */
@keyframes progress-fill {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.animate-progress-fill {
  animation: progress-fill linear;
  transform-origin: left;
}

/* Pause animation on hover */
.animate-progress-fill:hover {
  animation-play-state: paused;
}

/* Smooth button text transitions */
.button-text-container {
  overflow: hidden;
}

/* Smooth button width transitions */
.button-width-transition {
  transition: width 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94), min-width 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  min-width: fit-content;
}

.button-text-enter-active {
  transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transition-delay: 50ms;
}

.button-text-leave-active {
  transition: all 150ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.button-text-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.button-text-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}
</style>
